import { handleFileUpload } from '@onefootprint/idv2';
import { getErrorMessage } from '@onefootprint/request';
import { Button, useToast } from '@onefootprint/ui';
import type React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logger, trackAction } from '@/idv/utils';
import useProcessImage, { isProcessingResultError } from '../../hooks/use-process-image';
import type { ReceivedImagePayload } from '../../types';
type IdDocPhotoButtonsProp = {
  onComplete: (payload: ReceivedImagePayload) => void;
  uploadFirst?: boolean;
  hideCaptureButton?: boolean;
  hideUploadButton?: boolean;
  allowPdf?: boolean;
  hasBadConnectivity?: boolean;
  onTakePhoto?: () => void;
  fallbackUpload?: boolean;
  isNativeCameraCapture?: boolean;
  nativeCameraFacingMode?: 'environment' | 'user';
};
const IdDocPhotoButtons = ({
  onComplete,
  uploadFirst,
  hideCaptureButton,
  hideUploadButton,
  allowPdf,
  hasBadConnectivity,
  onTakePhoto,
  fallbackUpload,
  isNativeCameraCapture,
  nativeCameraFacingMode
}: IdDocPhotoButtonsProp) => {
  const {
    t
  } = useTranslation('idv', {
    keyPrefix: 'document-flow.components'
  });
  const {
    toast
  } = useToast();
  const uploadPhotoRef = useRef<HTMLInputElement | undefined>(undefined);
  const capturePhotoRef = useRef<HTMLInputElement | undefined>(undefined);
  const {
    processImageFile,
    acceptedFileFormats
  } = useProcessImage({
    allowPdf
  });
  const showCaptureButton = !hideCaptureButton;
  const showUploadButton = !hideUploadButton;
  const [isLoading, setIsLoading] = useState(false);
  const [captureMethod, setCaptureMethod] = useState<'take' | 'upload' | undefined>();
  const onProcessingDone = () => {
    setIsLoading(false);
    setCaptureMethod(undefined);
  };
  const handleProcess = (type: 'file' | 'photo') => {
    return async (event: React.ChangeEvent<HTMLInputElement>) => {
      try {
        Logger.info(`Processing ${type} image`);
        trackAction(`non-id-doc:process-${type}`);
        const files = event?.target?.files;
        const result = handleFileUpload({
          acceptedFileFormats,
          files,
          location: 'id-doc-photo-buttons'
        });
        if (!result.ok) {
          toast({
            variant: 'error',
            title: t($ => $.error.title),
            description: t($_0 => $_0.error.description.file_type_not_allowed)
          });
          event.target.value = '';
          return;
        }
        const validatedFile = result.file;
        setIsLoading(true);
        const processResult = await processImageFile(validatedFile, hasBadConnectivity);
        if (isProcessingResultError(processResult)) {
          Logger.error('Image upload failed. Uploaded image could not be processed', {
            location: 'id-doc-photo-buttons'
          });
          return;
        }
        const {
          file,
          extraCompressed
        } = processResult;
        Logger.info(`IdDocPhotoButtons: size of the processed file to be sent in machine event type 'receivedImage' is ${file.size}, file type ${file.type}`);
        onComplete({
          imageFile: file,
          extraCompressed,
          captureKind: captureMethod === 'upload' ? 'upload' : 'manual',
          forcedUpload: !!fallbackUpload && captureMethod === 'upload'
        });
      } catch (error) {
        Logger.error('Image upload failed', {
          location: 'id-doc-photo-buttons',
          error: getErrorMessage(error)
        });
      } finally {
        onProcessingDone();
      }
    };
  };
  const handleUpload = () => {
    Logger.info('Uploading image');
    trackAction('non-id-doc:upload-image');
    setCaptureMethod('upload');
    uploadPhotoRef.current?.click();
  };
  const handleTakePhoto = () => {
    if (isNativeCameraCapture) {
      handleNativeCameraCapture();
    } else {
      Logger.info('Taking photo with custom camera');
      trackAction('non-id-doc:take-photo-custom');
      onTakePhoto?.();
    }
  };
  const handleNativeCameraCapture = () => {
    Logger.info('Taking photo with native camera');
    trackAction('non-id-doc:take-photo-native');
    setCaptureMethod('take');
    capturePhotoRef.current?.click();
  };
  return <div className="w-full flex flex-col gap-y-3">
      {!!uploadFirst && showUploadButton && <Button fullWidth variant="primary" onClick={handleUpload} loading={isLoading && captureMethod === 'upload'} disabled={isLoading} size="large" data-dd-action-name="doc:upload-photo">
          {allowPdf ? t($_1 => $_1['photo-upload-buttons']['upload-file'].title) : t($_2 => $_2['photo-upload-buttons']['upload-photo'].title)}
        </Button>}
      {showCaptureButton && <Button fullWidth onClick={handleTakePhoto} variant={uploadFirst ? 'secondary' : 'primary'} loading={isLoading && captureMethod === 'take'} disabled={isLoading} size="large" data-dd-action-name="doc:take-photo">
          {t($_3 => $_3['photo-upload-buttons']['take-photo'].title)}
        </Button>}
      {!uploadFirst && showUploadButton && <Button fullWidth variant={hideCaptureButton ? 'primary' : 'secondary'} onClick={handleUpload} loading={isLoading && captureMethod === 'upload'} disabled={isLoading} size="large" data-dd-action-name="doc:upload-photo">
          {allowPdf ? t($_4 => $_4['photo-upload-buttons']['upload-file'].title) : t($_5 => $_5['photo-upload-buttons']['upload-photo'].title)}
        </Button>}
      <input ref={uploadPhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept={acceptedFileFormats.join(',')} onChange={handleProcess('file')} aria-label="file-input" className="hidden" />
      <input ref={capturePhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept="image/*" onChange={handleProcess('photo')} aria-label="photo-input" capture={nativeCameraFacingMode} className="hidden" />
    </div>;
};
export default IdDocPhotoButtons;